/**
 * 请求核心：注入令牌、归一错误、401 重建会话后重试一次。
 *
 * 这一层刻意不抽成共享包（见 docs/项目开发规范.md 第二节），
 * drama-ui-admin 会各写一份。下面两条最容易写歪，改动时对照检查：
 *   - 401 要重建会话并重试一次，不能直接把用户丢出去；
 *   - 403 是门禁不是错误，要交给页面渲染引导，不能弹错误提示。
 */
import type { components } from './schema.gen'

export type ErrorCode = components['schemas']['ErrorCode']
export type GateDenied = components['schemas']['GateDenied']

const BASE = '/api/v1'

/** 统一的失败类型。code 供 i18n 映射高棉文，detail 只进日志。 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'unknown',
    readonly detail?: string,
  ) {
    super(`${status} ${code}`)
    this.name = 'ApiError'
  }
}

/** 门禁未通过。不是错误——调用方据 gate 渲染引导。 */
export class GateError extends Error {
  constructor(readonly denied: GateDenied) {
    super(denied.gate)
    this.name = 'GateError'
  }
}

let token: string | null = null
let renew: (() => Promise<string | null>) | null = null

export function setToken(next: string | null): void {
  token = next
}

/** 注册会话续期方式。由 useSession 在启动时装上。 */
export function setRenewer(fn: (() => Promise<string | null>) | null): void {
  renew = fn
}

interface Options {
  method?: 'GET' | 'POST'
  body?: unknown
  query?: Record<string, string | number | undefined>
  /** 会话接口自身不参与 401 重试，否则会无限套娃 */
  skipRenew?: boolean
}

export async function request<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, query, skipRenew } = options

  const url = new URL(BASE + path, window.location.origin)
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
  }

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {}
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (token) headers['Authorization'] = `Bearer ${token}`
    return fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  }

  let res = await send()

  // 401 = 令牌无效或过期（服务端刻意不静默降级成匿名）。重建会话再试一次。
  if (res.status === 401 && !skipRenew && renew) {
    const next = await renew()
    if (next) {
      token = next
      res = await send()
    }
  }

  if (res.status === 204) return undefined as T
  const payload = await res.json().catch(() => null)

  if (res.status === 403 && payload && 'gate' in payload) {
    throw new GateError(payload as GateDenied)
  }
  if (!res.ok) {
    const e = payload as { code?: ErrorCode; message?: string } | null
    throw new ApiError(res.status, e?.code ?? 'unknown', e?.message)
  }
  return payload as T
}
